import { Box, Button, ButtonSize } from '@bechtel/gds';
import { MilestoneStatusCode } from '@bechtel/types';
import styled, { css } from 'styled-components';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import blockedPattern from '@bechtel/client-assets/dist/images/pattern-red.svg';
import { WeldMilestoneStylingProps } from '../types';

interface MilestoneActionContainerProps {
  isReassign: boolean | undefined;
}

const getCardStyles = (props: WeldMilestoneStylingProps) => {
  const { status, isFitupMilestone, isFitupRequired } = props;
  switch (status) {
    case MilestoneStatusCode.REQUESTED:
      return css`
        background-color: ${({ theme }) => theme.colors.yellow[100]};
        border-color: ${({ theme }) => theme.colors.yellow[300]};
      `;
    case MilestoneStatusCode.NOT_APPLICABLE:
      return css`
        background-color: ${({ theme }) => theme.colors.grey[100]};
        border-color: ${({ theme }) => theme.colors.grey[300]};
      `;
    case MilestoneStatusCode.CLAIMED:
      return css`
        background-color: ${({ theme }) => theme.colors.orange[100]};
        border-color: ${({ theme }) => theme.colors.orange[300]};
      `;
    case MilestoneStatusCode.APPROVED:
      return css`
        background-color: ${({ theme }) => theme.colors.green[100]};
        border-color: ${({ theme }) => theme.colors.green[300]};
      `;

    case MilestoneStatusCode.REJECTED:
      if (isFitupMilestone) {
        return isFitupRequired
          ? css`
              background-color: ${({ theme }) => theme.colors.purple[100]};
              border-color: ${({ theme }) => theme.colors.purple[300]};
            `
          : css`
              background-color: ${({ theme }) => theme.colors.grey[100]};
              border-color: ${({ theme }) => theme.colors.grey[300]};
            `;
      } else {
        return css`
          background-color: ${({ theme }) => theme.colors.purple[100]};
          border-color: ${({ theme }) => theme.colors.purple[300]};
        `;
      }
    case MilestoneStatusCode.BLOCKED:
      return css`
        background-color: ${({ theme }) => theme.colors.red[100]};
        border-color: ${({ theme }) => theme.colors.red[300]};
      `;
    default:
      return props.isAssigned
        ? css`
            background-color: ${({ theme }) => theme.colors.bechtel.blue[100]};
            border: 1px solid ${({ theme }) => theme.colors.bechtel.blue[300]};
          `
        : css`
            background-color: ${({ theme }) => theme.colors.grey[100]};
            border-color: ${({ theme }) => theme.colors.grey[300]};
          `;
  }
};

const getTitleStyles = (props: WeldMilestoneStylingProps) => {
  const { status, isFitupMilestone, isFitupRequired } = props;
  switch (status) {
    case MilestoneStatusCode.REQUESTED:
      return css`
        background-color: ${({ theme }) => theme.colors.yellow[200]};
        border-bottom-color: ${({ theme }) => theme.colors.yellow[300]};
      `;
    case MilestoneStatusCode.NOT_APPLICABLE:
      return css`
        background-color: ${({ theme }) => theme.colors.grey[200]};
        border-bottom-color: ${({ theme }) => theme.colors.grey[300]};
      `;
    case MilestoneStatusCode.CLAIMED:
      return css`
        background-color: ${({ theme }) => theme.colors.orange[200]};
        border-bottom-color: ${({ theme }) => theme.colors.orange[300]};
      `;
    case MilestoneStatusCode.APPROVED:
      return css`
        background-color: ${({ theme }) => theme.colors.green[200]};
        border-bottom-color: ${({ theme }) => theme.colors.green[300]};
      `;

    case MilestoneStatusCode.REJECTED:
      if (isFitupMilestone) {
        return isFitupRequired
          ? css`
              background-color: ${({ theme }) => theme.colors.purple[200]};
              border-bottom-color: ${({ theme }) => theme.colors.purple[300]};
            `
          : css`
              background-color: ${({ theme }) => theme.colors.grey[200]};
              border-bottom-color: ${({ theme }) => theme.colors.grey[300]};
            `;
      } else {
        return css`
          background-color: ${({ theme }) => theme.colors.purple[200]};
          border-bottom-color: ${({ theme }) => theme.colors.purple[300]};
        `;
      }
    case MilestoneStatusCode.BLOCKED:
      return css`
        background-color: ${({ theme }) => theme.colors.red[200]};
        border-bottom-color: ${({ theme }) => theme.colors.red[300]};
      `;
    default:
      return props.isAssigned
        ? css`
            background-color: ${({ theme }) => theme.colors.bechtel.blue[200]};
            border-bottom: 1px solid ${({ theme }) => theme.colors.blue[300]};
          `
        : css`
            background-color: ${({ theme }) => theme.colors.grey[200]};
            border-bottom: 1px solid ${({ theme }) => theme.colors.grey[300]};
          `;
  }
};

const getIndicatorStyles = (props: WeldMilestoneStylingProps) => {
  const { status, isFitupRequired, isFitupMilestone } = props;
  switch (status) {
    case MilestoneStatusCode.REQUESTED:
      return css`
        background-color: ${({ theme }) => theme.colors.yellow[500]};
      `;
    case MilestoneStatusCode.NOT_APPLICABLE:
      return css`
        background-color: ${({ theme }) => theme.colors.grey[500]};
      `;
    case MilestoneStatusCode.CLAIMED:
      return css`
        background-color: ${({ theme }) => theme.colors.orange[500]};
      `;
    case MilestoneStatusCode.APPROVED:
      return css`
        background-color: ${({ theme }) => theme.colors.green[500]};
      `;
    case MilestoneStatusCode.REJECTED:
      if (isFitupMilestone) {
        return isFitupRequired
          ? css`
              background-color: ${({ theme }) => theme.colors.purple[500]};
            `
          : css`
              background-color: ${({ theme }) => theme.colors.grey[500]};
            `;
      } else {
        return css`
          background-color: ${({ theme }) => theme.colors.purple[500]};
        `;
      }
    case MilestoneStatusCode.BLOCKED:
      return css`
        background-color: ${({ theme }) => theme.colors.red[400]};
        background-image: url(${blockedPattern});
        background-repeat: repeat;
        background-size: ${({ theme }) => theme.fontSizes[500]};
      `;
    default:
      return props.isAssigned
        ? css`
            background-color: ${({ theme }) => theme.colors.bechtel.blue[500]};
          `
        : css`
            background-color: ${({ theme }) => theme.colors.grey[500]};
          `;
  }
};

function getActionStyles({ status, isAssigned, isFitupMilestone, isFitupRequired }: WeldMilestoneStylingProps) {
  switch (status) {
    case MilestoneStatusCode.REQUESTED:
      return css`
        background-color: ${({ theme }) => theme.colors.yellow[300]};
        color: ${({ theme }) => theme.colors.yellow[900]};

        &:hover {
          background-color: ${({ theme }) => theme.colors.yellow[200]};
        }

        &:active {
          background-color: ${({ theme }) => theme.colors.yellow[400]};
        }
      `;
    case MilestoneStatusCode.NOT_APPLICABLE:
      return css`
        background-color: ${({ theme }) => theme.colors.grey[300]};
        color: ${({ theme }) => theme.colors.grey[900]};

        &:hover {
          background-color: ${({ theme }) => theme.colors.grey[200]};
        }

        &:active {
          background-color: ${({ theme }) => theme.colors.grey[400]};
        }
      `;
    case MilestoneStatusCode.CLAIMED:
      return css`
        background-color: ${({ theme }) => theme.colors.orange[300]};
        color: ${({ theme }) => theme.colors.orange[900]};

        &:hover {
          background-color: ${({ theme }) => theme.colors.orange[200]};
        }

        &:active {
          background-color: ${({ theme }) => theme.colors.orange[400]};
        }
      `;
    case MilestoneStatusCode.APPROVED:
      return css`
        background-color: ${({ theme }) => theme.colors.green[300]};
        color: ${({ theme }) => theme.colors.green[900]};

        &:hover {
          background-color: ${({ theme }) => theme.colors.green[200]};
        }

        &:active {
          background-color: ${({ theme }) => theme.colors.green[400]};
        }
      `;
    case MilestoneStatusCode.REJECTED:
      if (isFitupMilestone) {
        return isFitupRequired
          ? css`
              background-color: ${({ theme }) => theme.colors.purple[300]};
              color: ${({ theme }) => theme.colors.purple[900]};
              &:hover {
                background-color: ${({ theme }) => theme.colors.purple[200]};
              }
              &:active {
                background-color: ${({ theme }) => theme.colors.purple[400]};
              }
            `
          : css`
              background-color: ${({ theme }) => theme.colors.grey[300]};
              color: ${({ theme }) => theme.colors.grey[900]};

              &:hover {
                background-color: ${({ theme }) => theme.colors.grey[200]};
              }

              &:active {
                background-color: ${({ theme }) => theme.colors.grey[400]};
              }
            `;
      } else {
        return css`
          background-color: ${({ theme }) => theme.colors.purple[300]};
          color: ${({ theme }) => theme.colors.purple[900]};
          &:hover {
            background-color: ${({ theme }) => theme.colors.purple[200]};
          }
          &:active {
            background-color: ${({ theme }) => theme.colors.purple[400]};
          }
        `;
      }
    case MilestoneStatusCode.BLOCKED:
      return css`
        background-color: ${({ theme }) => theme.colors.red[300]};
        color: ${({ theme }) => theme.colors.red[900]};

        &:hover {
          background-color: ${({ theme }) => theme.colors.red[200]};
        }

        &:active {
          background-color: ${({ theme }) => theme.colors.red[400]};
        }
      `;
    default:
      return isAssigned
        ? css`
            background-color: ${({ theme }) => theme.colors.blue[300]};
            color: ${({ theme }) => theme.colors.blue[900]};

            &:hover {
              background-color: ${({ theme }) => theme.colors.blue[200]};
            }

            &:active {
              background-color: ${({ theme }) => theme.colors.blue[400]};
            }
          `
        : css`
            background-color: ${({ theme }) => theme.colors.grey[300]};
            color: ${({ theme }) => theme.colors.grey[900]};

            &:hover {
              background-color: ${({ theme }) => theme.colors.grey[200]};
            }

            &:active {
              background-color: ${({ theme }) => theme.colors.grey[400]};
            }
          `;
  }
}

export const WeldMilestoneCardStyled = styled.div<WeldMilestoneStylingProps>`
  border-radius: ${({ theme }) => theme.radii[200]};
  border-width: 1px;
  border-style: solid;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative;
  min-height: 224px;
  ${getCardStyles};
  ${(props: WeldMilestoneStylingProps) =>
    props.disabled &&
    css`
      border-style: dashed;
      background-color: ${({ theme }) => theme.colors.grey[100]};
      border-color: ${({ theme }) => theme.colors.grey[500]};
      opacity: 0.5;
    `}
`;

export const WeldMilestoneIndicatorStyled = styled.div<WeldMilestoneStylingProps>`
  height: ${({ theme }) => theme.spacings[300]};
  ${getIndicatorStyles};
  ${(props: WeldMilestoneStylingProps) =>
    props.disabled &&
    css`
      background-color: ${({ theme }) => theme.colors.grey[200]};
    `}
`;

export const WeldMilestoneTitleStyled = styled.div<WeldMilestoneStylingProps>`
  ${({ theme }) => css`
    border-bottom-width: 1px;
    border-bottom-style: solid;
    font-size: ${theme.fontSizes[400]};
    font-weight: ${theme.fontWeights[600]};
    padding: ${theme.spacings[300]};
    color: ${theme.colors.grey[1000]};
    font-family: ${theme.fontFamilies.graphik};
    text-align: center;
    position: relative;
    ${getTitleStyles};
    ${(props: WeldMilestoneStylingProps) =>
      props.disabled &&
      css`
        border-bottom-style: dashed;
        border-color: ${theme.colors.grey[500]};
      `}
  `}
`;

export const WeldMilestoneAttachmentContainerStyled = styled(Box)`
  position: absolute;
  ${({ theme }) => css`
    top: ${theme.spacings[400]};
    right: ${theme.spacings[400]};
  `}
`;

export const WeldMilestoneBodyStyled = styled.div`
  flex-grow: 1;
  padding: ${({ theme }) => theme.spacings[400]};
`;

export const WeldMilestoneActionStyled = styled(Button).attrs({
  size: ButtonSize.SMALL,
})<WeldMilestoneStylingProps>`
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.2),
    0 2px 1px -1px rgba(0, 0, 0, 0.12),
    0 1px 1px 0 rgba(0, 0, 0, 0.14);
  ${getActionStyles};
`;

export const MilestoneActionButtonStyled = styled(Button).attrs({
  size: ButtonSize.SMALL,
})<WeldMilestoneStylingProps>`
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.2),
    0 2px 1px -1px rgba(0, 0, 0, 0.12),
    0 1px 1px 0 rgba(0, 0, 0, 0.14);
  ${getActionStyles};
`;

export const MilestoneActionContainerStyled = styled(Box)<MilestoneActionContainerProps>`
  ${({ isReassign, theme }) =>
    isReassign
      ? css`
          margin-left: ${theme.spacings[400]};
        `
      : css`
          flex-grow: 1;
        `}
`;

export const AttachmentsBoxStyled = styled(Box)`
  ${({ theme }) => css`
    position: absolute;
    top: ${theme.spacings[500]};
    right: ${theme.spacings[300]};
  `}
`;
